import React, { useState, useRef, useEffect } from 'react';
import pdfComparatorIcon from '../assets/pdf-comparator-icon.svg';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import { Spinner } from '@cloudscape-design/components';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { addUsage } from './add-usage.js';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const PdfComparator = ({ isNew }) => {
  const [visible, setVisible] = useState(false);
  const [prodFile, setProdFile] = useState(null);
  const [testFile, setTestFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [differences, setDifferences] = useState([]);
  const [prodPdfBytes, setProdPdfBytes] = useState(null);
  const [testPdfBytes, setTestPdfBytes] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [prodPreview, setProdPreview] = useState(null);
  const [testPreview, setTestPreview] = useState(null);
  const [viewMode, setViewMode] = useState('side-by-side'); // 'side-by-side' or 'diff-list'
  const [noDifferences, setNoDifferences] = useState(false);

  const prodCanvasRef = useRef(null);
  const testCanvasRef = useRef(null);

  const toggleModal = () => {
    setVisible(!visible);
    if (visible) resetState();
  };

  const resetState = () => {
    setProdFile(null);
    setTestFile(null);
    setDifferences([]);
    setLoading(false);
    setProdPdfBytes(null);
    setTestPdfBytes(null);
    setDownloading(false);
    setCurrentPage(1);
    setTotalPages(1);
    setProdPreview(null);
    setTestPreview(null);
    setNoDifferences(false);
  };

  const extractText = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument(arrayBuffer).promise;
    const pages = [];
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      // Replace colons with space to separate labels from values (e.g., "Number:PL5262907815" -> "Number PL5262907815")
      const text = textContent.items.map((item) => item.str).join(' ').replace(/:/g, ' ');
      pages.push({
        pageNumber: i,
        text: text,
      });
    }
    return { pages, bytes: new Uint8Array(arrayBuffer), numPages: pdf.numPages };
  };

  const findDifferences = (prodPages, testPages) => {
    const diffs = [];
    const maxPages = Math.max(prodPages.length, testPages.length);

    for (let i = 0; i < maxPages; i++) {
      const prodText = prodPages[i]?.text || '';
      const testText = testPages[i]?.text || '';
      const prodWords = prodText.split(/\s+/).filter(Boolean);
      const testWords = testText.split(/\s+/).filter(Boolean);

      const prodSet = new Set(prodWords);
      const testSet = new Set(testWords);
      const matchedTestWords = new Set();

      // Find words in prod that don't exist in test OR have a similar match
      prodWords.forEach((word) => {
        if (word.length <= 1) return;
        if (testSet.has(word)) return; // Exact match exists, skip

        // Look for similar/containing match
        const matchIdx = findClosestMatch(word, testWords);
        if (matchIdx !== -1 && !matchedTestWords.has(testWords[matchIdx])) {
          diffs.push({ page: i + 1, type: 'edit', oldText: word, newText: testWords[matchIdx] });
          matchedTestWords.add(testWords[matchIdx]);
        } else {
          diffs.push({ page: i + 1, type: 'removed', oldText: word, newText: '' });
        }
      });

      testWords.forEach((word) => {
        if (!prodSet.has(word) && word.length > 1 && !matchedTestWords.has(word)) {
          diffs.push({ page: i + 1, type: 'added', oldText: '', newText: word });
        }
      });

      // Compare decimal numbers
      const prodNumbers = prodText.match(/\d+\.\d+/g) || [];
      const testNumbers = testText.match(/\d+\.\d+/g) || [];
      const prodNumSet = new Set(prodNumbers);
      const testNumSet = new Set(testNumbers);

      prodNumbers.forEach((num) => {
        if (!testNumSet.has(num) && !diffs.some((d) => d.oldText === num)) {
          diffs.push({ page: i + 1, type: 'removed', oldText: num, newText: '' });
        }
      });

      testNumbers.forEach((num) => {
        if (!prodNumSet.has(num) && !diffs.some((d) => d.newText === num)) {
          diffs.push({ page: i + 1, type: 'added', oldText: '', newText: num });
        }
      });
    }
    return diffs;
  };

  const findClosestMatch = (word, words) => {
    let bestIdx = -1, bestScore = 0;
    for (let idx = 0; idx < words.length; idx++) {
      const w = words[idx];
      // Check if one contains the other (for PL5262907815 vs 5262907815)
      if (word.length > 5 && w.length > 5 && word !== w && (word.includes(w) || w.includes(word))) {
        return idx; // Return immediately for contains match
      }
      const score = similarity(word, w);
      if (score > 0.5 && score < 1 && score > bestScore) {
        bestScore = score;
        bestIdx = idx;
      }
    }
    return bestIdx;
  };

  const similarity = (s1, s2) => {
    const longer = s1.length > s2.length ? s1 : s2;
    const shorter = s1.length > s2.length ? s2 : s1;
    if (longer.length === 0) return 1.0;
    let costs = [];
    for (let i = 0; i <= s1.length; i++) {
      let lastValue = i;
      for (let j = 0; j <= s2.length; j++) {
        if (i === 0) costs[j] = j;
        else if (j > 0) {
          let newValue = costs[j - 1];
          if (s1.charAt(i - 1) !== s2.charAt(j - 1)) newValue = Math.min(newValue, lastValue, costs[j]) + 1;
          costs[j - 1] = lastValue;
          lastValue = newValue;
        }
      }
      if (i > 0) costs[s2.length] = lastValue;
    }
    return (longer.length - costs[s2.length]) / longer.length;
  };

  const renderPage = async (bytes, pageNum, canvasRef, highlightTexts = [], highlightColor = 'green', otherPdfBytes = null) => {
    const pdf = await pdfjsLib.getDocument(bytes).promise;
    const page = await pdf.getPage(pageNum);
    const scale = 1.2;
    const viewport = page.getViewport({ scale });
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext('2d');
    await page.render({ canvasContext: ctx, viewport }).promise;

    if (highlightTexts.length > 0) {
      // Get text items from other PDF to check for identical items
      let otherTextItems = new Set();
      if (otherPdfBytes) {
        const otherPdf = await pdfjsLib.getDocument(otherPdfBytes).promise;
        if (pageNum <= otherPdf.numPages) {
          const otherPage = await otherPdf.getPage(pageNum);
          const otherContent = await otherPage.getTextContent();
          otherTextItems = new Set(otherContent.items.map((item) => item.str.trim()));
        }
      }

      const textContent = await page.getTextContent();
      const color = highlightColor === 'green' ? 'rgba(0, 200, 0, 0.4)' : 'rgba(255, 50, 50, 0.4)';
      const borderColor = highlightColor === 'green' ? '#1b5e20' : '#b71c1c';

      textContent.items.forEach((item) => {
        const itemText = item.str.trim();
        if (!itemText) return;

        // Check if this text item contains any of the diff words
        const matchedWord = highlightTexts.find((diffWord) => {
          return itemText === diffWord || itemText.includes(diffWord);
        });

        if (matchedWord) {
          // Skip only if this EXACT text item exists in BOTH PDFs (unchanged)
          if (otherTextItems.has(itemText)) return;

          const tx = pdfjsLib.Util.transform(viewport.transform, item.transform);
          const x = tx[4];
          const y = tx[5] - (item.height || 10) * scale;
          const width = (item.width || matchedWord.length * 6) * scale;
          const height = (item.height || 12) * scale + 2;

          ctx.fillStyle = color;
          ctx.fillRect(x - 1, y - 1, width + 2, height + 2);
          ctx.strokeStyle = borderColor;
          ctx.lineWidth = 1.5;
          ctx.strokeRect(x - 1, y - 1, width + 2, height + 2);
        }
      });
    }
  };

  useEffect(() => {
    if (viewMode !== 'side-by-side') return;
    const timer = setTimeout(() => {
      if (prodPdfBytes && prodCanvasRef.current) {
        const prodTexts = differences
          .filter((d) => d.page === currentPage)
          .flatMap((d) => {
            const texts = [];
            if (d.oldText) texts.push(d.oldText);
            if (d.type === 'added' && d.newText && /\d{5,}/.test(d.newText)) texts.push(d.newText);
            return texts;
          });
        renderPage(prodPdfBytes, currentPage, prodCanvasRef, prodTexts, 'green', testPdfBytes);
      }
      if (testPdfBytes && testCanvasRef.current) {
        // For red: highlight all diffs with newText (edits + added)
        const testTexts = differences
          .filter((d) => d.page === currentPage && d.newText)
          .map((d) => d.newText);
        renderPage(testPdfBytes, currentPage, testCanvasRef, testTexts, 'red', prodPdfBytes);
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [currentPage, prodPdfBytes, testPdfBytes, differences, viewMode]);

  const handleCompare = async () => {
    if (!prodFile || !testFile) return;
    setLoading(true);
    try {
      const [prodData, testData] = await Promise.all([extractText(prodFile), extractText(testFile)]);
      setProdPdfBytes(prodData.bytes);
      setTestPdfBytes(testData.bytes);
      setTotalPages(Math.max(prodData.numPages, testData.numPages));
      const diffs = findDifferences(prodData.pages, testData.pages);
      setDifferences(diffs);
      setNoDifferences(diffs.length === 0);
      addUsage('AWESOME PDF TOOLS', '1.0', 1, window.location.href);
    } catch (err) {
      console.error('Compare error:', err);
    } finally {
      setLoading(false);
    }
  };

  const downloadHighlightedPdf = async (type) => {
    setDownloading(true);
    try {
      const bytes = type === 'prod' ? prodPdfBytes : testPdfBytes;
      const otherBytes = type === 'prod' ? testPdfBytes : prodPdfBytes;
      if (!bytes) return;

      const highlightColor = type === 'prod' ? 'green' : 'red';
      const pdfDoc = await PDFDocument.create();
      const srcPdf = await pdfjsLib.getDocument(bytes).promise;
      const otherPdf = otherBytes ? await pdfjsLib.getDocument(otherBytes).promise : null;
      const numPages = srcPdf.numPages;

      const tempCanvas = document.createElement('canvas');
      const ctx = tempCanvas.getContext('2d');

      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        const page = await srcPdf.getPage(pageNum);
        const scale = 2;
        const viewport = page.getViewport({ scale });

        tempCanvas.width = viewport.width;
        tempCanvas.height = viewport.height;
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

        await page.render({ canvasContext: ctx, viewport }).promise;

        // Get highlight texts - same logic as useEffect
        const highlightTexts = type === 'prod'
          ? differences.filter((d) => d.page === pageNum).flatMap((d) => {
              const texts = [];
              if (d.oldText) texts.push(d.oldText);
              if (d.type === 'added' && d.newText && /\d{5,}/.test(d.newText)) texts.push(d.newText);
              return texts;
            })
          : differences.filter((d) => d.page === pageNum && d.newText).map((d) => d.newText);

        if (highlightTexts.length > 0) {
          // Get other PDF text items to check for identical items
          let otherTextItems = new Set();
          if (otherPdf && pageNum <= otherPdf.numPages) {
            const otherPage = await otherPdf.getPage(pageNum);
            const otherContent = await otherPage.getTextContent();
            otherTextItems = new Set(otherContent.items.map((item) => item.str.trim()));
          }

          const textContent = await page.getTextContent();
          const color = highlightColor === 'green' ? 'rgba(0, 200, 0, 0.4)' : 'rgba(255, 50, 50, 0.4)';
          const borderColor = highlightColor === 'green' ? '#1b5e20' : '#b71c1c';

          textContent.items.forEach((item) => {
            const itemText = item.str.trim();
            if (!itemText) return;

            const matchedWord = highlightTexts.find((diffWord) => {
              return itemText === diffWord || itemText.includes(diffWord);
            });

            if (matchedWord) {
              // Skip if exact text item exists in both PDFs
              if (otherTextItems.has(itemText)) return;

              const tx = pdfjsLib.Util.transform(viewport.transform, item.transform);
              const x = tx[4];
              const y = tx[5] - (item.height || 10) * scale;
              const width = (item.width || matchedWord.length * 6) * scale;
              const height = (item.height || 12) * scale + 2;
              ctx.fillStyle = color;
              ctx.fillRect(x - 1, y - 1, width + 2, height + 2);
              ctx.strokeStyle = borderColor;
              ctx.lineWidth = 2;
              ctx.strokeRect(x - 1, y - 1, width + 2, height + 2);
            }
          });
        }

        const imgData = tempCanvas.toDataURL('image/png');
        const pngImage = await pdfDoc.embedPng(imgData);
        const pdfPage = pdfDoc.addPage([viewport.width, viewport.height]);
        pdfPage.drawImage(pngImage, { x: 0, y: 0, width: viewport.width, height: viewport.height });
      }

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${type}-highlighted.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download error:', err);
    } finally {
      setDownloading(false);
    }
  };

  const pageDiffs = differences.filter((d) => d.page === currentPage);

  return (
    <div>
      <div className="service-card" onClick={toggleModal}>
        {isNew && <span className="badge badge-new">NEW</span>}
        <div className="service-card-icon"><img src={pdfComparatorIcon} alt="PDF Comparator" loading="lazy" /></div>
        <h3 className="service-card-title">PDF Comparator</h3>
        <p className="service-card-desc">Compare two PDFs and highlight text differences</p>
        <div className="service-card-action">
          <span>Get Started</span>
          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" /></svg>
        </div>
      </div>

      <Modal onDismiss={toggleModal} visible={visible} size="max"
        footer={<Box float="right"><SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={toggleModal}>Close</Button>
          {!differences.length && <Button variant="primary" disabled={!prodFile || !testFile || loading} onClick={handleCompare}>{loading ? <Spinner /> : 'Compare PDFs'}</Button>}
        </SpaceBetween></Box>}
        header="PDF Comparator">
        <div>
          {!differences.length && !loading && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="bordered-box">
                <h6 className="container-header">📄 Production PDF (Original)</h6>
                <FormField label="Upload the original PDF">
                  <input type="file" accept=".pdf" onChange={(e) => setProdFile(e.target.files[0])} />
                </FormField>
                {prodFile && <div style={{ marginTop: '8px', color: '#037f0c', fontSize: '13px' }}>✓ {prodFile.name}</div>}
              </div>
              <div className="bordered-box">
                <h6 className="container-header">📄 Test PDF (Modified)</h6>
                <FormField label="Upload the test PDF">
                  <input type="file" accept=".pdf" onChange={(e) => setTestFile(e.target.files[0])} />
                </FormField>
                {testFile && <div style={{ marginTop: '8px', color: '#037f0c', fontSize: '13px' }}>✓ {testFile.name}</div>}
              </div>
            </div>
          )}

          {loading && <div style={{ textAlign: 'center', padding: '40px' }}><Spinner size="large" /><p>Comparing PDFs...</p></div>}

          {noDifferences && (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
              <h3 style={{ color: '#2e7d32', marginBottom: '8px' }}>Both PDFs are identical!</h3>
              <p style={{ color: '#666' }}>No differences found between the uploaded documents.</p>
              <Button variant="primary" onClick={resetState} style={{ marginTop: '16px' }}>Compare New Files</Button>
            </div>
          )}

          {differences.length > 0 && (
            <div>
              {/* Controls */}
              <div className="bordered-box" style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Button disabled={currentPage <= 1} onClick={() => setCurrentPage((p) => p - 1)}>◀ Prev</Button>
                    <span style={{ fontWeight: '600' }}>Page {currentPage} / {totalPages}</span>
                    <Button disabled={currentPage >= totalPages} onClick={() => setCurrentPage((p) => p + 1)}>Next ▶</Button>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Button variant={viewMode === 'side-by-side' ? 'primary' : 'normal'} onClick={() => setViewMode('side-by-side')}>Side-by-Side</Button>
                    <Button variant={viewMode === 'diff-list' ? 'primary' : 'normal'} onClick={() => setViewMode('diff-list')}>Diff List</Button>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Button disabled={downloading} onClick={() => downloadHighlightedPdf('prod')}>⬇️ Prod PDF</Button>
                    <Button disabled={downloading} onClick={() => downloadHighlightedPdf('test')}>⬇️ Test PDF</Button>
                    <Button variant="link" onClick={resetState}>New Compare</Button>
                  </div>
                </div>
              </div>

              {/* Summary */}
              <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                <div style={{ flex: 1, padding: '10px', backgroundColor: '#e8f5e9', borderRadius: '6px', textAlign: 'center' }}>
                  <strong style={{ color: '#2e7d32' }}>{differences.filter((d) => d.oldText).length}</strong> <span style={{ fontSize: '12px' }}>Removed/Changed</span>
                </div>
                <div style={{ flex: 1, padding: '10px', backgroundColor: '#ffebee', borderRadius: '6px', textAlign: 'center' }}>
                  <strong style={{ color: '#c62828' }}>{differences.filter((d) => d.newText).length}</strong> <span style={{ fontSize: '12px' }}>Added/Changed</span>
                </div>
                <div style={{ flex: 1, padding: '10px', backgroundColor: '#fff3e0', borderRadius: '6px', textAlign: 'center' }}>
                  <strong style={{ color: '#e65100' }}>{pageDiffs.length}</strong> <span style={{ fontSize: '12px' }}>On This Page</span>
                </div>
              </div>

              {viewMode === 'side-by-side' ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="bordered-box" style={{ padding: '8px' }}>
                    <div style={{ textAlign: 'center', marginBottom: '8px', fontWeight: '600', color: '#2e7d32' }}>📄 Production (Original)</div>
                    <div style={{ border: '2px solid #4caf50', borderRadius: '4px', overflow: 'auto', maxHeight: '500px' }}>
                      <canvas ref={prodCanvasRef} style={{ display: 'block', width: '100%' }} />
                    </div>
                  </div>
                  <div className="bordered-box" style={{ padding: '8px' }}>
                    <div style={{ textAlign: 'center', marginBottom: '8px', fontWeight: '600', color: '#c62828' }}>📄 Test (Modified)</div>
                    <div style={{ border: '2px solid #f44336', borderRadius: '4px', overflow: 'auto', maxHeight: '500px' }}>
                      <canvas ref={testCanvasRef} style={{ display: 'block', width: '100%' }} />
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  {differences.map((diff, idx) => (
                    <div key={idx} style={{ border: '1px solid #e0e0e0', borderRadius: '6px', marginBottom: '8px', overflow: 'hidden', cursor: 'pointer' }} onClick={() => setCurrentPage(diff.page)}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', backgroundColor: '#f5f5f5' }}>
                        <span style={{ fontWeight: '600', fontSize: '13px' }}>Page {diff.page}</span>
                        <span style={{ backgroundColor: diff.type === 'edit' ? '#fff3cd' : diff.type === 'added' ? '#d4edda' : '#f8d7da', padding: '2px 6px', borderRadius: '3px', fontSize: '11px' }}>
                          {diff.type === 'edit' ? 'Edit' : diff.type === 'added' ? 'Added' : 'Removed'}
                        </span>
                      </div>
                      <div style={{ padding: '8px', display: 'flex', gap: '8px' }}>
                        {diff.oldText && <div style={{ flex: 1, backgroundColor: '#e8f5e9', padding: '6px', borderRadius: '4px', fontSize: '12px' }}><span style={{ color: '#666', fontSize: '10px' }}>OLD:</span> <span style={{ color: '#2e7d32', textDecoration: diff.type === 'edit' ? 'line-through' : 'none' }}>{diff.oldText}</span></div>}
                        {diff.newText && <div style={{ flex: 1, backgroundColor: '#ffebee', padding: '6px', borderRadius: '4px', fontSize: '12px' }}><span style={{ color: '#666', fontSize: '10px' }}>NEW:</span> <span style={{ color: '#c62828', fontWeight: '500' }}>{diff.newText}</span></div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default PdfComparator;
